/* Wrap fetch to manage concurrency and error handling.
   Concurrency limit was added to prevent exhausting browser resources, which we've seen
   in editor mode when the local server quickly serves many requests in parallel, leading
   to the browser error:  net::ERR_INSUFFICIENT_RESOURCES
*/

import logger from './logger'

// Shared state (one queue + counter for the whole module)
const queue           = []    // pending fetch jobs
let   inFlight        = 0     // currently-running fetches
let   concurrencyCap  = 100   // default limit

// Drive the queue
const runNext = () => {
  if (inFlight >= concurrencyCap) {
    return
  }

  const job = queue.shift()
  if (!job) {
    return       // nothing waiting
  }

  const { url, options, acceptedErrorCodes, resolve, reject } = job
  inFlight += 1

  ;(async () => {
    try {
      logger.info(`Fetching ${url}`)
      const resp = await fetch(url, options)

      if (resp.ok) {
        logger.debug(`Successfully fetched "${url}"`)
        return resp
      }

      // Log unexpected status codes
      const isAccepted =
        acceptedErrorCodes?.length &&
        acceptedErrorCodes.includes(resp.status)

      if (!isAccepted) {
        logger.error(`Failed to fetch "${url}" with status ${resp.status}`)
      }

      return resp
    } catch (err) {
      logger.info(`Failed to fetch "${url}"`)
      logger.error(err)
      throw err
    }
  })()
    .then(resolve)
    .catch(reject)
    .finally(() => {
      inFlight -= 1
      runNext()          // kick off another queued fetch
    })
}

// Fetch handler that manages concurrency and error handling
const fetchHandler = (
  url,
  options = {},
  acceptedErrorCodes = [],
  maxConcurrent = concurrencyCap   // optional limit on concurrency
) =>
  new Promise((resolve, reject) => {
    // Allow caller to change the concurrency cap on the fly
    if (Number.isFinite(maxConcurrent) && maxConcurrent > 0) {
      concurrencyCap = maxConcurrent
    }

    // Enqueue this request and start processing
    queue.push({ url, options, acceptedErrorCodes, resolve, reject })
    runNext()
  })

export default fetchHandler
